import { Done, TimerOutlined } from "@mui/icons-material";
import { IconButton, TextField } from "@mui/material";
import {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import {
  DEFAULT_LIVE_MAP_GRID_REFRESH_INTERVAL_SEC,
  DEFAULT_LIVE_MAP_MAP_REFRESH_INTERVAL_SEC,
  liveMapPollingUiAtom,
  liveMapRefreshGridIntervalSecOverrideAtom,
  liveMapRefreshMapIntervalSecOverrideAtom,
  Log,
  MILLISECONDS_IN_SECOND,
  MIN_LIVE_MAP_REFRESH_INTERVAL_SEC,
  resolveLiveMapRefreshIntervalSec,
  TOOLTIP_DELAY_BUTTONS,
  useSystemParams,
} from "@ryde/shared";

import { LiveMapContext } from "../../context";
import { LiveMapPollProgressRing } from "./live-map.poll-progress-ring";
import {
  LiveMapRefreshIntervalControlStyled,
  LiveMapRefreshIntervalFieldsStyled,
  LiveMapRefreshIntervalInputGroupStyled,
  LiveMapRefreshIntervalSecLabelStyled,
} from "./live-map.refresh-interval.control.styled";
import { liveMapRefreshIntervalTimerIconSx } from "./live-map.styled";

export type TLiveMapRefreshIntervalVariant = "grid" | "map";

export interface ILiveMapRefreshIntervalControlProps {
  variant: TLiveMapRefreshIntervalVariant;
  /** Timer ring only — hides sec input (mobile map toolbar). */
  hideSecInput?: boolean;
}

export const LiveMapRefreshIntervalControl: FC<
  ILiveMapRefreshIntervalControlProps
> = ({ variant, hideSecInput = false }) => {
  const { t } = useTranslation();
  const { liveMapRefreshGridIntervalSec, liveMapRefreshMapIntervalSec } =
    useSystemParams();
  const [gridRefreshIntervalOverride, setGridRefreshIntervalOverride] =
    useRecoilState(liveMapRefreshGridIntervalSecOverrideAtom);
  const [mapRefreshIntervalOverride, setMapRefreshIntervalOverride] =
    useRecoilState(liveMapRefreshMapIntervalSecOverrideAtom);
  const { gridRefreshIntervalSec, mapRefreshIntervalSec } =
    useContext(LiveMapContext);
  const {
    lastSynchroGridDate,
    lastSynchroMapDate,
    isGridPollingRunning,
    mapPollingRideIds,
  } = useRecoilValue(liveMapPollingUiAtom);

  const isGrid = variant === "grid";

  const resolvedGridRefreshIntervalSec = useMemo(
    () =>
      resolveLiveMapRefreshIntervalSec({
        overrideValue: gridRefreshIntervalOverride,
        systemParamValue: liveMapRefreshGridIntervalSec,
        fallback: DEFAULT_LIVE_MAP_GRID_REFRESH_INTERVAL_SEC,
      }),
    [gridRefreshIntervalOverride, liveMapRefreshGridIntervalSec],
  );

  const resolvedMapRefreshIntervalSec = useMemo(
    () =>
      resolveLiveMapRefreshIntervalSec({
        overrideValue: mapRefreshIntervalOverride,
        systemParamValue: liveMapRefreshMapIntervalSec,
        fallback: DEFAULT_LIVE_MAP_MAP_REFRESH_INTERVAL_SEC,
      }),
    [mapRefreshIntervalOverride, liveMapRefreshMapIntervalSec],
  );

  const resolvedRefreshIntervalSec = isGrid
    ? resolvedGridRefreshIntervalSec
    : resolvedMapRefreshIntervalSec;
  const overrideValue = isGrid
    ? gridRefreshIntervalOverride
    : mapRefreshIntervalOverride;
  const setOverrideValue = isGrid
    ? setGridRefreshIntervalOverride
    : setMapRefreshIntervalOverride;

  useEffect(() => {
    if (
      typeof overrideValue === "number" &&
      Number.isFinite(overrideValue) &&
      overrideValue < MIN_LIVE_MAP_REFRESH_INTERVAL_SEC
    ) {
      setOverrideValue(MIN_LIVE_MAP_REFRESH_INTERVAL_SEC);
    }
  }, [overrideValue, setOverrideValue]);

  const [draftSec, setDraftSec] = useState<string>(
    String(resolvedRefreshIntervalSec),
  );

  useEffect(() => {
    setDraftSec(String(resolvedRefreshIntervalSec));
  }, [resolvedRefreshIntervalSec]);

  const parsedDraftSec = useMemo(() => {
    const parsed = Number(draftSec);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return undefined;
    }
    return Math.floor(parsed);
  }, [draftSec]);

  const isDraftBelowMinimum =
    parsedDraftSec !== undefined &&
    parsedDraftSec < MIN_LIVE_MAP_REFRESH_INTERVAL_SEC;
  const isDraftValid =
    parsedDraftSec !== undefined &&
    parsedDraftSec >= MIN_LIVE_MAP_REFRESH_INTERVAL_SEC;

  const isApplyEnabled =
    isDraftValid && parsedDraftSec !== resolvedRefreshIntervalSec;

  const minIntervalHint = t("rideMap.LIVE_MAP_REFRESH_INTERVAL_MIN", {
    minSeconds: MIN_LIVE_MAP_REFRESH_INTERVAL_SEC,
  });

  const onDraftChange = useEvent((event: ChangeEvent<HTMLInputElement>) => {
    setDraftSec(event.target.value);
  });

  const onApplyClick = useEvent(() => {
    if (parsedDraftSec === undefined) {
      return;
    }
    if (parsedDraftSec < MIN_LIVE_MAP_REFRESH_INTERVAL_SEC) {
      Log.warnAndToast(minIntervalHint, []);
      return;
    }
    setOverrideValue(parsedDraftSec);
  });

  const isMapPollActive = Boolean(mapPollingRideIds?.length);
  /** Mobile map toolbar: map poll only when rides are selected; otherwise grid poll drives the ring. */
  const useGridPollProgressRing = isGrid || (hideSecInput && !isMapPollActive);

  const tooltipKey = useGridPollProgressRing
    ? "rideMap.LIVE_MAP_REFRESH_INTERVAL_GRID_TOOLTIP"
    : "rideMap.LIVE_MAP_REFRESH_INTERVAL_MAP_TOOLTIP";

  const pollLastSyncIso = useGridPollProgressRing
    ? lastSynchroGridDate
    : lastSynchroMapDate;
  const activeRefreshIntervalSec = useGridPollProgressRing
    ? (gridRefreshIntervalSec ?? resolvedGridRefreshIntervalSec)
    : (mapRefreshIntervalSec ?? resolvedMapRefreshIntervalSec);
  const pollIntervalMs = activeRefreshIntervalSec * MILLISECONDS_IN_SECOND;
  const isPollActive = useGridPollProgressRing
    ? Boolean(isGridPollingRunning)
    : isMapPollActive;

  return (
    <LiveMapRefreshIntervalControlStyled data-variant={variant}>
      <LiveMapRefreshIntervalFieldsStyled>
        <FramerTooltip
          content={t(tooltipKey, {
            minSeconds: MIN_LIVE_MAP_REFRESH_INTERVAL_SEC,
          })}
          mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
        >
          <LiveMapPollProgressRing
            lastSyncIso={pollLastSyncIso}
            intervalMs={pollIntervalMs}
            isPollingActive={isPollActive}
          >
            <TimerOutlined sx={liveMapRefreshIntervalTimerIconSx} aria-hidden />
          </LiveMapPollProgressRing>
        </FramerTooltip>
        {!hideSecInput && (
          <LiveMapRefreshIntervalInputGroupStyled>
            <TextField
              type="number"
              size="small"
              value={draftSec}
              onChange={onDraftChange}
              error={isDraftBelowMinimum}
              helperText={isDraftBelowMinimum ? minIntervalHint : undefined}
              aria-label={t("rideMap.LIVE_MAP_REFRESH_INTERVAL_SEC")}
              slotProps={{
                htmlInput: {
                  min: MIN_LIVE_MAP_REFRESH_INTERVAL_SEC,
                },
              }}
            />
            <LiveMapRefreshIntervalSecLabelStyled aria-hidden>
              {t("rideMap.LIVE_MAP_REFRESH_INTERVAL_SEC")}
            </LiveMapRefreshIntervalSecLabelStyled>
            {isApplyEnabled && (
              <FramerTooltip
                content={t("rideMap.LIVE_MAP_REFRESH_INTERVAL_APPLY")}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={t("rideMap.LIVE_MAP_REFRESH_INTERVAL_APPLY")}
                    onClick={onApplyClick}
                  >
                    <Done fontSize="small" />
                  </IconButton>
                </span>
              </FramerTooltip>
            )}
          </LiveMapRefreshIntervalInputGroupStyled>
        )}
      </LiveMapRefreshIntervalFieldsStyled>
    </LiveMapRefreshIntervalControlStyled>
  );
};
